import React, { useEffect, useRef } from 'react';
import { FooterProps } from '../../types';
import { layout, colors } from '../../styles/designTokens';
import { useScrollAnimation } from '../../utils/useScrollAnimation';

const Footer: React.FC<FooterProps> = ({
  logoBatyaIcon,
  phone = '073-3026674',
  email = 'Yam0733026674@gmail.com',
}) => {
  const footerRef1 = useScrollAnimation({ animationType: 'fadeIn', threshold: 0.1, triggerOnce: true });
  const footerRef2 = useScrollAnimation({ animationType: 'fadeIn', threshold: 0.1, triggerOnce: true });
  const particlesRef = useRef<HTMLDivElement>(null);
  const footerSectionRef = useRef<HTMLDivElement>(null);

  // Animated Particles Effect - חלקיקים זוהרים כתומים (כמו ב-BranchesSection)
  useEffect(() => {
    if (!particlesRef.current || !footerSectionRef.current) return;

    const container = particlesRef.current;
    const section = footerSectionRef.current;
    const particles: Array<{
      element: HTMLDivElement;
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
    }> = [];

    // יצירת חלקיקים
    const createParticles = () => {
      const particleCount = 20; // ⚡ OPTIMIZED: הופחת ל-20 (55% פחות עומס!)
      const sectionRect = section.getBoundingClientRect();

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        const size = Math.random() * 4+ 4; // ⚡ OPTIMIZED: 3-6px - גדולים במתינות!
        const x = Math.random() * sectionRect.width;
        const y = Math.random() * sectionRect.height;

        particle.style.position = 'absolute';
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.borderRadius = '50%';
        particle.style.background = `radial-gradient(circle, 
          rgba(245, 182, 88, 0.7) 0%, 
          rgba(245, 182, 88, 0.5) 50%, 
          rgba(245, 182, 88, 0.2) 80%, 
          transparent 100%
        )`;
        particle.style.pointerEvents = 'none';
        particle.style.left = `${x}px`;
        particle.style.top = `${y}px`;
        particle.style.zIndex = '0';
        particle.style.opacity = String(Math.random() * 0.3 + 0.3); // 0.3-0.6 - עדין יותר

        container.appendChild(particle);

        particles.push({
          element: particle,
          x,
          y,
          vx: (Math.random() - 0.5) * 0.8, // מהירות אופקית - 0.8 (כמו שהיה)
          vy: (Math.random() - 0.5) * 0.8, // מהירות אנכית - 0.8 (כמו שהיה)
          size,
        });
      }
    };

    // אנימציה של החלקיקים
    let animationFrameId: number | null = null;
    let lastTime = performance.now();

    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      const sectionRect = section.getBoundingClientRect();

      particles.forEach(particle => {
        // עדכון מיקום
        particle.x += particle.vx * (deltaTime * 0.1);
        particle.y += particle.vy * (deltaTime * 0.1);

        // גבולות - חלקיקים חוזרים לצד השני
        if (particle.x < 0) particle.x = sectionRect.width;
        if (particle.x > sectionRect.width) particle.x = 0;
        if (particle.y < 0) particle.y = sectionRect.height;
        if (particle.y > sectionRect.height) particle.y = 0;

        // עדכון מיקום DOM
        particle.element.style.left = `${particle.x}px`;
        particle.element.style.top = `${particle.y}px`;

        // שינוי עדין בשקיפות (נשימה עדינה) - עדין יותר
        const opacity = 0.3 + Math.sin(currentTime * 0.001 + particle.x * 0.01) * 0.2;
        particle.element.style.opacity = String(Math.max(0.2, Math.min(0.6, opacity)));
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    createParticles();
    animationFrameId = requestAnimationFrame(animate);

    // טיפול בשינוי גודל החלון
    const handleResize = () => {
      const sectionRect = section.getBoundingClientRect();
      particles.forEach(particle => {
        particle.x = Math.min(particle.x, sectionRect.width);
        particle.y = Math.min(particle.y, sectionRect.height);
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      particles.forEach(particle => {
        if (particle.element.parentNode) {
          particle.element.parentNode.removeChild(particle.element);
        }
      });
    };
  }, []);

  return (
    <>
      {/* Footer Section 1 - Contact Info - עם fade-in animation - רקע שחור */}
      <div 
        ref={footerSectionRef}
        className="footer-section-1 relative overflow-hidden" 
        style={{ 
          backgroundColor: '#000000', 
          paddingTop: 'clamp(12px, 2vw, 20px)', 
          paddingBottom: 'clamp(12px, 2vw, 20px)',
          position: 'relative',
          overflowX: 'hidden', // מונע overflow אופקי
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* Animated Particles Background - חלקיקים זוהרים כתומים */}
        <div
          ref={particlesRef}
          className="absolute inset-0"
          style={{
            pointerEvents: 'none',
            zIndex: 0,
            overflow: 'hidden',
          }}
        />
        
        <div ref={footerRef1} className="container mx-auto px-4 relative z-10" style={{ maxWidth: layout.contentMaxWidth }}>
          <div className="flex md:grid md:grid-cols-3 gap-0 md:gap-6 justify-center items-center text-center">
            {/* ימין - ים ייבוא ושיווק */}
            <div className="footer-contact-item order-3 md:order-1">
              <p 
                className="font-normal transition-all duration-300 cursor-default"
                style={{
                  fontSize: 'clamp(0.75rem, 2vw, 1.125rem)',
                  background: colors.accentSpecial.textGradientRich,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  padding: '0 clamp(0px, 0.5vw, 4px)',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                ים ייבוא ושיווק
              </p>
            </div>
            {/* אמצע - טלפון */}
            <div className="footer-contact-item order-1 md:order-2">
              <a 
                href="tel:0733026674"
                className="font-normal transition-all duration-300"
                style={{
                  fontSize: 'clamp(0.75rem, 2vw, 1.125rem)',
                  background: colors.accentSpecial.textGradientRich,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textDecoration: 'none',
                  display: 'inline-block',
                  padding: '0 clamp(0px, 0.5vw, 4px)',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                073.302.6674
              </a>
            </div>
            {/* שמאל - אימייל */}
            {/* <div className="footer-contact-item order-2 md:order-3">
              <a 
                href="mailto:yam0733026674@gmail.com"
                className="font-normal transition-all duration-300"
                style={{
                  fontSize: 'clamp(0.75rem, 2vw, 1.125rem)',
                  background: colors.accentSpecial.textGradientRich,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textDecoration: 'none',
                  display: 'inline-block',
                  padding: '0 clamp(0px, 0.5vw, 4px)',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                yam0733026674@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div> */}


      {/* שמאל - אימייל */}
      <div className="footer-contact-item order-2 md:order-3">
        <a 
          href="https://mail.google.com/mail/u/0/?fs=1&tf=cm&source=mailto&to=yam0733026674@gmail.com"
          target="_blank"
          rel="noopener noreferrer"
          className="font-normal transition-all duration-300"
          style={{
            fontSize: 'clamp(0.75rem, 2vw, 1.125rem)',
            background: colors.accentSpecial.textGradientRich,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textDecoration: 'none',
            display: 'inline-block',
            padding: '0 clamp(0px, 0.5vw, 4px)',
            borderRadius: '8px',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          yam0733026674@gmail.com
        </a>
      </div>
                </div>
              </div>
            </div>


      {/* Footer Section 2 - Credits - עם fade-in animation - רקע כתום */}
      <div 
        ref={footerRef2} 
        className="footer-section-2"
        style={{ 
          background: colors.accentSpecial.buttonGradientDiagonal, 
          paddingTop: 'clamp(12px, 2vw, 20px)', 
          paddingBottom: 'clamp(12px, 2vw, 20px)',
          overflowX: 'hidden', // מונע overflow אופקי
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <div className="container mx-auto px-4" style={{ maxWidth: layout.contentMaxWidth }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" style={{ direction: 'rtl' }}>
            {/* ימין - קישורים */}
            <div className="footer-links flex flex-row items-center justify-center gap-2 md:gap-6 order-3 md:order-1">
              <a 
                href="/accessibility"
                className="footer-link font-normal underline relative transition-all duration-300"
                aria-label="מעבר לדף הצהרת נגישות"
                style={{ 
                  fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                  color: '#000000',
                  backgroundImage: 'linear-gradient(90deg, transparent 0%, transparent 100%)',
                  backgroundSize: '0% 2px',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'bottom right',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundSize = '100% 2px';
                  e.currentTarget.style.backgroundImage = `linear-gradient(90deg, ${colors.gold.base}80 0%, ${colors.gold.mediumLight} 50%, ${colors.gold.base}80 100%)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundSize = '0% 2px';
                  e.currentTarget.style.backgroundImage = 'linear-gradient(90deg, transparent 0%, transparent 100%)';
                }}
              >
                הצהרת נגישות
              </a>
              <span style={{ color: '#000000' }}>|</span>
              <a 
                href="/website-terms"
                className="footer-link font-normal underline relative transition-all duration-300"
                aria-label="מעבר לדף תקנון אתר ומשלוחים"
                style={{ 
                  fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                  color: '#000000',
                  backgroundImage: 'linear-gradient(90deg, transparent 0%, transparent 100%)',
                  backgroundSize: '0% 2px',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'bottom right',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundSize = '100% 2px';
                  e.currentTarget.style.backgroundImage = `linear-gradient(90deg, ${colors.gold.base}80 0%, ${colors.gold.mediumLight} 50%, ${colors.gold.base}80 100%)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundSize = '0% 2px';
                  e.currentTarget.style.backgroundImage = 'linear-gradient(90deg, transparent 0%, transparent 100%)';
                }}
              >
                תקנון אתר ומשלוחים
              </a>
            </div>
            {/* שמאל - פיתוח ועיצוב */}
            {/* <div className="footer-credit-item order-3 md:order-3 flex items-center justify-start gap-3" style={{ direction: 'rtl', marginRight: 0, marginLeft: 0 }}>
              <p className="font-normal" style={{ fontSize: 'clamp(0.75rem, 1.8vw, 1rem)', color: '#000000', margin: 0, display: 'flex', gap: 'clamp(8px, 1.5vw, 16px)', direction: 'rtl' }}>
                <span>פיתוח&עיצוב מרים</span>
              </p>
            </div> */}

            <a 
              href="https://mail.google.com/mail/u/0/?fs=1&tf=cm&source=mailto&to=m0533123308@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-normal order-1 md:order-3"
              style={{ 
                fontSize: 'clamp(0.75rem, 1.8vw, 1rem)', 
                color: '#000000', 
                textDecoration: 'underline',
                transition: 'opacity 0.3s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              פיתוח&עיצוב מרים
            </a>

          </div>
        </div>
      </div>
    </>
  );
};

export default Footer;

